// Global Instructions Rule Applied!
//
// Catalog of dimensions a user can pick for the Fund Investment Detail grid.
// Order here is the default presentation order in the dimension picker.
// Values match the at__get_measures p_aggregation strings (or, for
// account-attribute dims, strings handled by view__fund_investment_detail in
// jsonb space).

export interface AggregationDimensionOption {
  value: string
  label: string
  /** Native = passed through to at__get_measures. Otherwise the wrapper handles it. */
  native: boolean
}

export const FUND_INVESTMENT_AGGREGATION_DIMENSIONS: AggregationDimensionOption[] = [
  { value: 'tranche',         label: 'Tranche',         native: true },
  { value: 'company',         label: 'Company',         native: true },
  { value: 'industry',        label: 'Industry',        native: false },
  { value: 'country',         label: 'Country',         native: false },
  { value: 'state',           label: 'State / Province',native: false },
  { value: 'city',            label: 'City',            native: false },
  { value: 'valuation_type',  label: 'Valuation Type',  native: true },
  { value: 'security_type',   label: 'Security Type',   native: true },
  { value: 'security',        label: 'Security',        native: true },
  { value: 'vintage',         label: 'Vintage',         native: true },
  { value: 'ultimate_parent', label: 'Ultimate Parent', native: true }
]

export function aggregationDimensionLabel(value: string): string {
  return FUND_INVESTMENT_AGGREGATION_DIMENSIONS.find((d) => d.value === value)?.label ?? value
}

/**
 * Maps each aggregation dimension to the `cfg.reporting_column_defaults` row
 * that carries its display label. After Phase 2, `(module_id, field_name)` is
 * UNIQUE on the attribute table, so a single field_name + module pair resolves
 * unambiguously — the legacy `linkType` disambiguator is no longer needed.
 */
export interface DimLabelFieldRef {
  fieldName: string
}

export const FUND_INVESTMENT_DIM_LABEL_FIELD: Record<string, DimLabelFieldRef> = {
  fund:            { fieldName: 'funds__name' },
  tranche:         { fieldName: 'tranches__name' },
  company:         { fieldName: 'accounts__name' },
  industry:        { fieldName: 'accounts__industry__label' },
  country:         { fieldName: 'accounts__country__label' },
  state:           { fieldName: 'accounts__state' },
  city:            { fieldName: 'accounts__city__label' },
  valuation_type:  { fieldName: 'cfg_asset_valuation_types__label' },
  security_type:   { fieldName: 'cfg_security_types__label' },
  security:        { fieldName: 'securities__name' },
  vintage:         { fieldName: 'vintage__label' },
  ultimate_parent: { fieldName: 'up_tranches__name' }
}

/** Set of all dim-label field_names (for data-driven column hiding). */
export const FUND_INVESTMENT_DIM_LABEL_FIELD_NAMES: ReadonlySet<string> = new Set(
  Object.values(FUND_INVESTMENT_DIM_LABEL_FIELD).map((r) => r.fieldName)
)

/**
 * Resolve the `cfg.reporting_column_defaults` row for a given dim, restricted
 * to the accounts module. Returns undefined when nothing matches.
 */
export function resolveDimLabelColumn(
  dim: string,
  reportingColumnDefaults: any[],
  modules: any[]
): any | undefined {
  const ref = FUND_INVESTMENT_DIM_LABEL_FIELD[dim]
  if (!ref) return undefined
  const accountsModuleId = modules.find((m: any) => m?.key === 'accounts')?.id
  return reportingColumnDefaults.find(
    (c: any) =>
      c?.field_name === ref.fieldName &&
      (!accountsModuleId || c.module_id === accountsModuleId)
  )
}

/** Default aggregation. Matches the auto-sort the table opens with: company asc, date_invested asc. */
export const FUND_INVESTMENT_DEFAULT_AGGREGATION: string[] = ['company', 'tranche']
